// =============================================================================
// FILE: packages/renderer/src/pages/PublicAcademyPage.tsx
// PURPOSE: 🎓 R2 Academy — PUBLIC educational portal (no login). Reachable at
//   #academy / /academy. Explains BIM OPS, R2, capabilities, the RCDD future
//   model, tutorials (placeholders), FAQ, and an "Ask R2 Academy" chatbot that
//   reuses the existing /api/academy/chat RAG backend.
// =============================================================================

import { useRef, useState, type JSX } from 'react';
import {
  GraduationCap, Route as RouteIcon, Layers, Boxes, FileText, FileBarChart2,
  Bot, Sparkles, ChevronDown, Send, MessageCircle, X, PlayCircle,
  BookOpen, Workflow, Video, Building2, ShieldCheck, ArrowRight, Rss,
} from 'lucide-react';
import { NewsletterModal } from '../components/marketing/NewsletterModal.js';

const BACKEND: string = (() => {
  const w = typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__ as string
    : '';
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

const NAV = [
  { id: 'welcome',      label: 'Welcome' },
  { id: 'paths',        label: 'Learning Paths' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'future',       label: 'RCDD Future' },
  { id: 'tutorials',    label: 'Tutorials' },
  { id: 'faq',          label: 'FAQ' },
];

const PATHS = [
  {
    title: 'Getting Started with BIM OPS',
    topics: ['What is BIM OPS?', 'Why BIM automation matters', 'The future role of the RCDD', 'How AI assists infrastructure design'],
  },
  {
    title: 'Understanding R2',
    topics: ['What is R2?', 'R2 Brain', 'R2 Academy', 'AI Agents', 'Infrastructure Intelligence'],
  },
  {
    title: 'BIM OPS Capabilities',
    topics: ['Route Designer', 'Discipline Drawings', 'BIM Coordination', 'Documentation', 'Reports & Analytics', 'Multi-Agent Systems'],
  },
];

const CAPS = [
  { icon: RouteIcon, title: 'AI Infrastructure Route Designer',
    what: 'Automatically proposes optimal pathways and conduit runs between spaces.',
    why: 'Manual routing is slow and error-prone; AI checks distance limits and capacity instantly.',
    how: 'You set intent and constraints; R2 proposes routes and flags 90 m / pull-box issues for your approval.',
    cases: ['New building risers', 'Campus backbone planning', 'Retrofit pathway audits'] },
  { icon: Layers, title: 'Automated Discipline Drawings',
    what: 'Generates plan drawings, rack elevations and detail sheets from live project data.',
    why: 'Eliminates hours of repetitive drafting and keeps drawings in sync with the model.',
    how: 'Select a project and output type; R2 assembles the drawing from your assets.',
    cases: ['Rack elevations', 'Riser diagrams', 'Submittal drawing sets'] },
  { icon: Boxes, title: 'BIM Coordination',
    what: 'Coordinates telecom infrastructure across disciplines and detects clashes.',
    why: 'Catches conflicts before install, avoiding costly field changes.',
    how: 'R2 reviews the combined model and reports clashes with context.',
    cases: ['Ceiling congestion checks', 'Trade coordination', 'Pre-construction reviews'] },
  { icon: Building2, title: 'Infrastructure Planning',
    what: 'Plans capacity, spaces, and distribution for whole facilities.',
    why: 'Turns high-level objectives into structured, standards-aware plans.',
    how: 'Describe the facility goal; R2 proposes an infrastructure layout to refine.',
    cases: ['Data-centre rooms', 'Multi-floor offices', 'Campus expansions'] },
  { icon: FileText, title: 'Documentation Automation',
    what: 'Produces schedules, reports, and BIM model updates automatically.',
    why: 'Documentation is the biggest time sink — automation frees experts for design.',
    how: 'R2 compiles documentation from project data on demand.',
    cases: ['Cable schedules', 'Close-out packages', 'As-built updates'] },
  { icon: FileBarChart2, title: 'Reports & Analytics',
    what: 'Generates quantity takeoffs, estimates, and certification reports.',
    why: 'Accurate counts and reports improve bids and compliance.',
    how: 'R2 tallies materials and formats reports for hand-off.',
    cases: ['Bid takeoffs', 'Compliance audits', 'Billing packages'] },
  { icon: Bot, title: 'Future Multi-Agent Systems',
    what: 'Specialized AI agents that will collaborate on a design together.',
    why: 'Parallel expert agents dramatically compress design cycles.',
    how: 'Route Designer, BIM Coordinator, Standards Checker and more work as a team under your direction.',
    cases: ['End-to-end design', 'Simultaneous coordination', 'Automated QA'] },
];

const FUTURE = [
  { h: 'What Exists Today', items: ['Manual interpretation of project requirements', 'Reviewing architectural drawings', 'Creating pathways and layouts', 'Producing CAD/BIM documentation', 'Coordinating across disciplines', 'Calculations and validations', 'Managing standards & specifications', 'Reviewing changes and resolving conflicts'] },
  { h: 'What Will Become Automated', items: ['AI-assisted infrastructure planning & routing', 'Automated plan drawings, risers, schedules & reports', 'Automated clash detection & coordination', 'Change tracking & design validation', 'Intelligent standards lookup & best-practice guidance'] },
  { h: 'What Remains Human-Critical', items: ['Vision & strategy — client goals, technology direction', 'Professional judgment — risk, cost, reliability, expansion', 'Leadership — technology strategist & infrastructure architect', 'Ethics & responsibility — public safety, quality, compliance'] },
  { h: 'What BIM OPS Becomes in 5 Years', items: ['A Digital Infrastructure Operating System', 'Describe an objective → review AI solutions → approve designs', 'Multi-agent collaboration across specialized AIs', 'The RCDD moves from document creator to infrastructure visionary'] },
];

const TUTORIALS = [
  { icon: BookOpen, title: 'Beginner Guides', desc: 'Step-by-step introductions to core workflows.' },
  { icon: Workflow, title: 'Workflow Examples', desc: 'Real end-to-end design scenarios.' },
  { icon: Video, title: 'Video Tutorials', desc: 'Watch R2 in action.', video: true },
  { icon: Building2, title: 'Case Studies', desc: 'How teams deliver faster with BIM OPS.' },
  { icon: ShieldCheck, title: 'Best Practices', desc: 'Standards-aligned tips from the field.' },
];

const FAQ = [
  { q: 'What is BIM OPS?', a: 'BIM OPS (the RCD² Engine) is an AI-assisted platform that automates the repetitive parts of telecommunications and infrastructure design — routing, drawings, coordination, documentation and reporting — so professionals focus on higher-value decisions.' },
  { q: 'Who is BIM OPS for?', a: 'RCDD-certified designers, engineers, BIM coordinators, and technology professionals who plan and document infrastructure.' },
  { q: 'Does BIM OPS replace designers?', a: 'No. BIM OPS amplifies human expertise. The RCDD remains the decision-maker for design intent, code compliance, and professional judgment. AI provides intelligence; the RCDD provides wisdom.' },
  { q: 'How does AI help RCDD professionals?', a: 'It automates drafting, routing, clash detection, and documentation, and offers standards lookup and design recommendations — cutting hours of repetitive work.' },
  { q: 'What makes R2 different?', a: 'R2 pairs a living intelligence centre (R2 Brain) with a docs-grounded trainer (R2 Academy), and is built to evolve into a multi-agent infrastructure platform — grounded in real engineering standards, not generic AI.' },
  { q: 'Can BIM OPS work with existing workflows?', a: 'Yes. It is designed to fit alongside your current CAD/BIM tools and processes, augmenting them rather than forcing a full replacement.' },
];

function useChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Array<{ role: 'user' | 'ai'; text: string }>>([
    { role: 'ai', text: 'Hi! I\u2019m R2 Academy. Ask me anything about using BIM OPS.' },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef<string | undefined>(undefined);

  const send = async (q: string): Promise<void> => {
    const question = q.trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: 'user', text: question }]);
    setInput('');
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/academy/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, session_id: sessionRef.current }),
      });
      const d = await r.json();
      sessionRef.current = d.session_id ?? sessionRef.current;
      setMsgs((m) => [...m, { role: 'ai', text: d.answer ?? 'This feature is not yet documented.' }]);
    } catch {
      setMsgs((m) => [...m, { role: 'ai', text: 'R2 Academy is temporarily unavailable. Please try again.' }]);
    } finally { setBusy(false); }
  };
  return { open, setOpen, msgs, input, setInput, busy, send };
}

export function PublicAcademyPage(): JSX.Element {
  const chat = useChat();
  const [newsOpen, setNewsOpen] = useState(false);
  const scrollTo = (id: string): void => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const [openCap, setOpenCap] = useState<number | null>(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div data-testid="public-academy-page" className="min-h-screen w-full text-zinc-200" style={{ background: '#08090c' }}>
      {/* Sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: 'rgba(8,9,12,0.85)', borderBottom: '1px solid rgba(45,181,168,0.18)' }}>
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center gap-3">
          <button onClick={() => { window.location.hash = ''; window.location.reload(); }} data-testid="academy-home-link" className="flex items-center gap-2 group">
            <span className="grid place-items-center h-8 w-8 rounded-lg" style={{ background: 'rgba(45,181,168,0.12)', border: '1px solid rgba(45,181,168,0.35)' }}>
              <GraduationCap className="h-4 w-4 text-teal-300" />
            </span>
            <span className="font-semibold tracking-tight text-[15px]">R2 Academy</span>
          </button>
          <nav className="ml-auto hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} className="px-3 py-1.5 text-[12px] rounded-lg text-zinc-400 hover:text-teal-300 hover:bg-white/5 transition">{n.label}</button>
            ))}
          </nav>
          <button onClick={() => setNewsOpen(true)} data-testid="academy-subscribe-nav" className="hidden sm:inline-flex ml-auto md:ml-1 items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold text-teal-300 transition hover:bg-teal-400/10" style={{ border: '1px solid rgba(45,181,168,0.4)' }}><Rss className="h-3.5 w-3.5" /> Subscribe to R2 News</button>
          <button onClick={() => { window.location.hash = ''; window.location.reload(); }} data-testid="academy-enter-app" className="ml-1 px-3.5 py-1.5 rounded-full text-[12px] font-semibold text-zinc-900 transition hover:brightness-110" style={{ background: 'linear-gradient(90deg,#2dd4bf,#5eead4)' }}>Enter BIM OPS →</button>
        </div>
      </header>

      {/* Welcome / Hero */}
      <section id="welcome" className="max-w-5xl mx-auto px-5 pt-20 pb-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] mb-6" style={{ background: 'rgba(45,181,168,0.1)', border: '1px solid rgba(45,181,168,0.3)', color: '#5eead4' }}>
          <Sparkles className="h-3 w-3" /> Free Learning Centre
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
          Welcome to <span style={{ color: '#5eead4' }}>R2 Academy</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
          The learning centre for BIM OPS — where engineers, designers, and technology professionals
          explore the future of infrastructure design, powered by R2's living intelligence.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={() => scrollTo('paths')} className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-zinc-900" style={{ background: 'linear-gradient(90deg,#2dd4bf,#5eead4)' }}>Start Learning</button>
          <button onClick={() => setNewsOpen(true)} data-testid="academy-subscribe-hero" className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-teal-300 flex items-center gap-2" style={{ border: '1px solid rgba(45,181,168,0.4)' }}><Rss className="h-4 w-4" /> Join the R2 News Feed</button>
          <button onClick={() => chat.setOpen(true)} data-testid="academy-open-chat" className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-teal-300 flex items-center gap-2" style={{ border: '1px solid rgba(45,181,168,0.4)' }}><MessageCircle className="h-4 w-4" /> Ask R2 Academy</button>
        </div>
      </section>

      {/* Learning Paths */}
      <Section id="paths" title="Learning Paths" kicker="Structured tracks">
        <div className="grid gap-4 md:grid-cols-3">
          {PATHS.map((p) => (
            <div key={p.title} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="text-[15px] font-semibold text-teal-200 mb-3">{p.title}</h3>
              <ul className="space-y-2">
                {p.topics.map((t) => (
                  <li key={t} className="flex items-center gap-2 text-[13px] text-zinc-400"><ArrowRight className="h-3 w-3 text-teal-400 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Capabilities */}
      <Section id="capabilities" title="BIM OPS Capabilities" kicker="What R2 can do">
        <div className="space-y-2.5">
          {CAPS.map((c, i) => {
            const Icon = c.icon; const open = openCap === i;
            return (
              <div key={c.title} className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <button data-testid={`cap-toggle-${i}`} onClick={() => setOpenCap(open ? null : i)} className="w-full flex items-center gap-3 px-5 py-4 text-left">
                  <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0" style={{ background: 'rgba(45,181,168,0.1)', border: '1px solid rgba(45,181,168,0.3)' }}><Icon className="h-4 w-4 text-teal-300" /></span>
                  <span className="text-[14.5px] font-semibold text-zinc-100">{c.title}</span>
                  <ChevronDown className={`ml-auto h-4 w-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="px-5 pb-5 grid gap-3 sm:grid-cols-2 text-[13px]">
                    <Field label="What it does" text={c.what} />
                    <Field label="Why it matters" text={c.why} />
                    <Field label="How it changes the workflow" text={c.how} />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Example use cases</div>
                      <div className="flex flex-wrap gap-1.5">{c.cases.map((u) => <span key={u} className="px-2 py-0.5 rounded-full text-[11px] text-teal-200" style={{ background: 'rgba(45,181,168,0.08)', border: '1px solid rgba(45,181,168,0.25)' }}>{u}</span>)}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* RCDD Future Operating Model */}
      <Section id="future" title="RCDD Future Operating Model (v1)" kicker="The evolution of telecommunications design">
        <p className="text-[13.5px] text-zinc-400 max-w-3xl mb-6 leading-relaxed">
          BIM OPS does not replace the expertise, judgment, and responsibility of the RCDD. It amplifies human
          intelligence — automating repetitive processes so professionals focus on higher-value decisions.
          The future combines <span className="text-teal-300">human expertise + artificial intelligence + automation</span>.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {FUTURE.map((f) => (
            <div key={f.h} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="text-[14px] font-semibold text-teal-200 mb-3">{f.h}</h3>
              <ul className="space-y-1.5">{f.items.map((it) => <li key={it} className="flex items-start gap-2 text-[12.5px] text-zinc-400"><span className="mt-1.5 h-1 w-1 rounded-full bg-teal-400 shrink-0" /> {it}</li>)}</ul>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-[15px] font-medium text-zinc-300">AI provides intelligence. <span className="text-teal-300">The RCDD provides wisdom.</span></p>
      </Section>

      {/* Tutorials */}
      <Section id="tutorials" title="Tutorials" kicker="Coming soon">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TUTORIALS.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4 text-teal-300" /><h3 className="text-[14px] font-semibold text-zinc-100">{t.title}</h3></div>
                <p className="text-[12.5px] text-zinc-400 mb-3">{t.desc}</p>
                {t.video ? (
                  <div className="aspect-video rounded-lg grid place-items-center" style={{ background: 'rgba(0,0,0,0.4)', border: '1px dashed rgba(255,255,255,0.12)' }} data-testid="video-placeholder">
                    <div className="text-center text-zinc-500"><PlayCircle className="h-8 w-8 mx-auto mb-1 opacity-40" /><span className="text-[11px]">Video preview</span></div>
                  </div>
                ) : (
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide text-amber-300" style={{ background: 'rgba(252,211,77,0.1)', border: '1px solid rgba(252,211,77,0.3)' }}>Coming soon</span>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" title="Frequently Asked Questions" kicker="Quick answers">
        <div className="space-y-2.5 max-w-3xl">
          {FAQ.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={f.q} className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <button data-testid={`faq-toggle-${i}`} onClick={() => setOpenFaq(open ? null : i)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                  <span className="text-[13.5px] font-medium text-zinc-100">{f.q}</span>
                  <ChevronDown className={`ml-auto h-4 w-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && <p className="px-4 pb-4 text-[13px] text-zinc-400 leading-relaxed">{f.a}</p>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Public vs Private */}
      <Section id="access" title="Public vs Private Academy" kicker="Two levels of learning">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl p-5" style={{ background: 'rgba(45,181,168,0.05)', border: '1px solid rgba(45,181,168,0.25)' }}>
            <div className="text-[13px] font-semibold text-teal-200 mb-2">Public Academy (this page)</div>
            <ul className="space-y-1.5 text-[12.5px] text-zinc-400">{['Educational content', 'Product explanations', 'Feature demonstrations', 'Industry vision'].map((x) => <li key={x} className="flex items-center gap-2"><ArrowRight className="h-3 w-3 text-teal-400" /> {x}</li>)}</ul>
          </div>
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="text-[13px] font-semibold text-zinc-100 mb-2">Private Academy (inside BIM OPS)</div>
            <ul className="space-y-1.5 text-[12.5px] text-zinc-400">{['User-specific help', 'Software instructions', 'Project assistance', 'Documentation search', 'AI training assistant'].map((x) => <li key={x} className="flex items-center gap-2"><ArrowRight className="h-3 w-3 text-zinc-500" /> {x}</li>)}</ul>
          </div>
        </div>
      </Section>

      <section className="max-w-6xl mx-auto px-5 py-14 border-t text-center" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="text-[11px] uppercase tracking-[0.24em] text-teal-400/70 mb-1.5">Stay Connected with R2</div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Follow the evolution of BIM OPS</h2>
        <p className="text-[14px] text-zinc-400 max-w-xl mx-auto mb-6">Get product updates, R2 Academy articles, AI infrastructure trends, and roadmap announcements — before everyone else.</p>
        <button onClick={() => setNewsOpen(true)} data-testid="academy-subscribe-footer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] font-semibold text-zinc-900 hover:brightness-110 transition" style={{ background: 'linear-gradient(90deg,#2dd4bf,#5eead4)' }}><Rss className="h-4 w-4" /> Subscribe to R2 News</button>
      </section>

      <footer className="max-w-6xl mx-auto px-5 py-10 text-center text-[12px] text-zinc-600 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        RCD² Engine · BIM OPS — The Intelligence Hub for BIM &amp; Telecom Engineering
      </footer>

      <NewsletterModal open={newsOpen} onClose={() => setNewsOpen(false)} />

      {/* Ask R2 Academy — floating chatbot (reuses /api/academy/chat) */}
      {!chat.open && (
        <button data-testid="academy-chat-fab" onClick={() => chat.setOpen(true)} className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full text-[13px] font-semibold text-zinc-900 shadow-lg hover:brightness-110 transition" style={{ background: 'linear-gradient(90deg,#2dd4bf,#5eead4)' }}>
          <MessageCircle className="h-4 w-4" /> Ask R2 Academy
        </button>
      )}
      {chat.open && (
        <div data-testid="academy-chat-panel" className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm rounded-2xl overflow-hidden flex flex-col shadow-2xl" style={{ background: '#0c0e12', border: '1px solid rgba(45,181,168,0.3)', maxHeight: '70vh' }}>
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <GraduationCap className="h-4 w-4 text-teal-300" />
            <span className="text-[13px] font-semibold">R2 Academy</span>
            <button onClick={() => chat.setOpen(false)} className="ml-auto text-zinc-500 hover:text-zinc-200"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-auto px-3 py-3 space-y-2.5">
            {chat.msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-[12.5px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'text-zinc-900' : 'text-zinc-200'}`} style={{ background: m.role === 'user' ? '#5eead4' : 'rgba(255,255,255,0.05)' }}>{m.text}</div>
              </div>
            ))}
            {chat.busy && <div className="text-[12px] text-zinc-500 px-1">R2 Academy is typing…</div>}
          </div>
          <div className="px-3 pb-3">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {['How do I create a project?', 'Explain the Dashboard', 'What are Active Agents?'].map((s) => (
                <button key={s} onClick={() => void chat.send(s)} className="px-2 py-1 rounded-full text-[11px] text-teal-200" style={{ background: 'rgba(45,181,168,0.08)', border: '1px solid rgba(45,181,168,0.25)' }}>{s}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input data-testid="academy-chat-input" value={chat.input} onChange={(e) => chat.setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void chat.send(chat.input); }} placeholder="Ask how to use BIM OPS…" className="flex-1 h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-[13px] focus:outline-none focus:border-teal-400/50" />
              <button data-testid="academy-chat-send" onClick={() => void chat.send(chat.input)} disabled={chat.busy} className="h-10 w-10 grid place-items-center rounded-lg text-zinc-900 disabled:opacity-50" style={{ background: '#5eead4' }}><Send className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ id, title, kicker, children }: { id: string; title: string; kicker: string; children: React.ReactNode }): JSX.Element {
  return (
    <section id={id} className="max-w-6xl mx-auto px-5 py-14 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
      <div className="text-[11px] uppercase tracking-[0.24em] text-teal-400/70 mb-1.5">{kicker}</div>
      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, text }: { label: string; text: string }): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">{label}</div>
      <p className="text-zinc-400 leading-relaxed">{text}</p>
    </div>
  );
}
